/*global _ */
import loadJS from 'load-js'
import {fv} from '../../../lib/utils'
import {valueFormat, format, getFirst} from '../../../lib/formatters'

import Base from '../../Base'
import eventsFactory from '../../mixins/factory/events'

export const NAMESPACE = 'autocomplete_places'

export const EVENTS = {
  INPUT: 'input',
  PLACE_CHANGED: 'place_changed',
  PLACE: 'place',
  NO_RESULT: 'noresult',
}

let serviceLoader
const serviceUrl = 'https://maps.googleapis.com/maps/api/js?'

export class Places extends Base {
  static async loadService(options) {
    if (_.has(window, 'google.maps.places.Autocomplete')) {
      return Promise.resolve()
    }
    if (serviceLoader) {
      return serviceLoader
    }

    const opt = Object.assign({
      libraries: 'places'
    }, options || {})

    const parameters = Object.keys(opt)
      .map(key => `${encodeURIComponent(key)}=${encodeURIComponent(opt[key])}`)
      .join('&')

    const url = `${serviceUrl}${parameters}`
    return loadJS(url).catch(e => {
      serviceLoader = null
      console.warn('Error loading Places Autocomplete service', e)
    })
  }

  constructor(data, vm) {
    super(data, vm)
    this.init()
  }

  get Service() {
    return _.get(window, 'google.maps.places.Autocomplete')
  }

  get Geocoder() {
    return _.get(window, 'google.maps.Geocoder')
  }

  get GeoCircle() {
    return _.get(window, 'google.maps.Circle')
  }

  get ServiceEvents() {
    return _.get(window, 'google.maps.event')
  }

  get serviceApiOptions() {
    return _.omitBy({
      key: this.apiKey,
      v: this.version,
      callback: 'Function.prototype'
    }, _.isNil)
  }

  get serviceOptions() {
    //https://developers.google.com/maps/documentation/javascript/reference/places-widget#AutocompleteOptions
    if (!this.fields) {
      // https://developers.google.com/maps/documentation/javascript/reference/places-service#PlaceResult
      //address_components, adr_address
      this.fields = ['address_components', 'formatted_address']
    }
    if (!this.types) {
      //https://developers.google.com/maps/documentation/javascript/supported_types#table3
      // geocode, address, establishment, (regions), (cities)
      this.types = ['address']
    }
    const options = {
      fields: _.castArray(this.fields),
      types: _.castArray(this.types),
    }

    if (this.country) {
      options.componentRestrictions = {
        country: this.country
      }
    }
    return _.omitBy(options, _.isNil)
  }


  init() {
    this.listeners = fv(this.listeners, {})
    this.$_id = fv(this.$_id, _.uniqueId(`${NAMESPACE}_`))

    this.reset()
    this.initService()
    return this
  }

  reset(data) {
    data = _.merge({}, this.$$data, data || {})
    const props = [
      'apiKey',
      'version',

      'getElement',
      'element',

      'enableGeolocation',
      'enableGeocode',

      'value',

      'types',
      'fields',
      'country',

      'hydrate',
      'addressFields',
      'textValue',
      'currentPlace',
      'geolocateSet',
      'prepared',

    ]

    for (let prop of props) {
      this[prop] = data[prop]
    }

    // target element
    if (this.element) {
      this.getElement = async () => this.element
    }
    if (!this.getElement) {
      this.getElement = async () => {
        const vm = this.$$vm() || {}
        let el = vm.$el
        if (el) {
          if (el.tagName !== 'INPUT') {
            el = el.querySelector('input')
          }
          return el
        }
        return new Promise((resolve) => {
          vm.mounted(async function () {
            await this.$nextTick()
            resolve(this.$el)
          })
        })
      }
    }

    this.parsedAddressFields = _.merge({}, {
      street_number: 'short_name',
      route: 'long_name',
      sublocality_level_1: 'long_name',
      sublocality_level_2: 'long_name',
      sublocality_level_3: 'long_name',
      locality: 'long_name',
      administrative_area_level_1: 'short_name',
      administrative_area_level_2: 'short_name',
      administrative_area_level_3: 'short_name',
      postal_code: 'short_name'
    }, this.addressFields)

    this.service = null

    return this
  }

  initService() {
    const service = this.service
    if (!service) {
      const options = this.serviceApiOptions
      serviceLoader = Places.loadService(options)
      serviceLoader.then(() => {
        this.setupService()
      })
    } else {
      this.setupService()
    }
  }

  setupService() {
    this.getElement().then((elm) => {
      this.element = elm
      if (!this.element) {
        console.warn('Input element was not found in ' + this.component)
        return
      }

      const options = this.serviceOptions
      this.service = new this.Service(this.element, options)

      this.service.addListener(EVENTS.PLACE_CHANGED, () => this.onServiceResultSelect())
      this.geocoder = new this.Geocoder()
      this.geolocate()
    })
  }

  geolocate() {
    if (this.enableGeolocation && !this.geolocateSet) {
      if (!navigator.geolocation) {
        return
      }
      navigator.geolocation.getCurrentPosition((position) => {
        const geolocation = {
          lat: position.coords.latitude,
          lng: position.coords.longitude
        }
        const circle = new this.GeoCircle({
          center: geolocation,
          radius: position.coords.accuracy
        })
        if (this.enableGeocode) {
          this.geocoder.geocode({'location': geolocation}, (results, status) => {
            if (status === 'OK' && results.length) {
              this.textValue = results[1].formatted_address
              const pl = this.parsePlace(results[1])
              this.changePlace(pl)
            }
          })
        }
        this.autocomplete.setBounds(circle.getBounds())
        this.geolocateSet = true
      })
    }
  }

  parsePlace(place) {
    const returnData = {}

    if (place.formatted_address !== undefined) {
      returnData.formatted_address = place.formatted_address
      this.textValue = place.formatted_address
      // document.getElementById(this.id).value = place.formatted_address
    }

    if (place.address_components !== undefined) {
      // Get each component of the address from the place details
      for (let i = 0; i < place.address_components.length; i += 1) {
        const addressType = place.address_components[i].types[0]

        if (this.parsedAddressFields[addressType]) {
          const val = place.address_components[i][this.parsedAddressFields[addressType]]
          returnData[addressType] = val
        }
        if (addressType === 'country') {
          returnData.country = place.address_components[i]['long_name']
          returnData.country_code = place.address_components[i]['short_name']
        }
      }

      if (place.geometry) {
        returnData.latitude = place.geometry.location.lat()
        returnData.longitude = place.geometry.location.lng()
      }

      // additional fields available in google places results
      returnData.name = place.name
      returnData.formatted_address = place.formatted_address
      returnData.photos = place.photos
      returnData.place_id = place.place_id
      returnData.place = place
    }
    return returnData
  }

  changePlace(place, original) {
    this.emit(EVENTS.PLACE, place, original, this)
    this.textValue = place ? this.textValue : ''
    this.emit(EVENTS.INPUT, this.textValue, place, original, this)
    this.currentPlace = place
    this.hydrateFromPlace(place, original)
  }

  onServiceResultSelect() {
    this.hasDownBeenPressed = false
    const place = this.service.getPlace()
    this.emit(EVENTS.PLACE_CHANGED, place, this)
    const pl = this.parsePlace(place)
    this.changePlace(pl, place)
  }

  hydrateFromPlace(place, $original) {
    const $context = this.$$vm()
    const data = {...place, $original, $context, $this: this}
    const keyHydrator = (source, target) => {
      let value
      if (_.isObject(source)) {
        value = format.call(
          $context,
          source,
          '',
          {},
          data
        )
        if (_.isArray(value)) {
          value = value.join(' ')
        }
        if (_.isObject(value)) {
          value = value.value
        }
        value = _.trim(value || '').replace(/\s+?/g, ' ')

        // debugLog({value, source, target, data})
      } else {
        value = getFirst(source, place, $original, $context, this)
        // debugLog({value, source, target})
      }
      _.set($context, target, value)
    }

    const hydrate = this.hydrate
    if (hydrate) {
      if (_.isFunction(hydrate)) {
        hydrate(place, $original, this)
      } else {
        if (_.isObject(hydrate)) {
          if (_.isArray(hydrate)) {
            _.forOwn(hydrate, (item) => {
              keyHydrator(item.source, item.target)
            })
          } else {
            _.forOwn(hydrate, (key, targetKey) => {
              keyHydrator(key, targetKey)
            })
          }
        }
      }
    }
  }
}

const listenersMixin = eventsFactory(NAMESPACE, EVENTS)
Object.assign(Places.prototype, listenersMixin)

export default Places
